import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { listClassrooms } from "../services/classroom";
import { listEvaluations } from "../services/evaluation";
import { groupGradebooks, listGradebooksBySubject, publishGradebooksBySubjectClass } from "../services/gradebook";
import { exportGradebooksXls, exportPautaGeralXls } from "../services/reports";
import { canEditGradebookAnyStatus } from "../services/auth";
import { GRADEBOOK_STATUS_BADGE, GRADEBOOK_STATUS_LABEL_KEY } from "../utils/gradebookStatus";
import { useConfirm } from "../hook/useConfirm";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
// Matches CadastrarAvaliacaoLayer/ListaAvaliacoesLayer's PERIOD_OPTIONS — the
// gradebook's `period` field is copied straight from its evaluation's.
// Values match sge_infantil_web's `shifts` (src/pages/grade/list/index.js)
// and every gradebook/evaluation record migrated from it — real data uses
// these full labels as the `period` value, not "1"/"2"/"3".
const PERIOD_OPTIONS = [
  { value: "Primeiro Trimestre", label: "1º Trimestre" },
  { value: "Segundo Trimestre", label: "2º Trimestre" },
  { value: "Terceiro Trimestre", label: "3º Trimestre" },
];

const isGradeFilled = (value) => {
  if (value === null || value === undefined || value === "") return false;
  return !Number.isNaN(Number(value));
};

const rowKey = (row) => `${row.levelId}-${row.subjectId}-${row.classId}`;

// A group's `period` only comes back set when every gradebook in it belongs
// to the same trimestre (see routes/gradebook.js#/group/:sucursalId) — null
// means it's combining more than one (no Trimestre filter applied).
const periodLabel = (row, t) =>
  row.period == null ? t("nota.filter.multiplePeriods") : PERIOD_OPTIONS.find((p) => p.value === String(row.period))?.label || "-";

// Ported from sge_infantil_web/src/pages/grade/pauta/index.js: unlike Lista
// de Notas (one row per evaluation gradebook), Pautas shows one row per
// subject+turma — the school's overall "mapa de notas" for that subject —
// using the real backend routes the original screen relied on:
//   GET  /api/gradebook/group/:sucursalId?levelId&classId   (grouped status)
//   GET  /api/gradebook/subject/:sucursalId/:subjectId      (missing-grades expand)
//   PUT  /api/gradebook/publish/:sucursalId/:subjectId/:classId (bulk publish)
//   GET  /api/report/export-{pauta-geral,gradebooks}-xls/:sucursalId (Excel export)
const PautasLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();

  const [year, setYear] = useState(currentYear);
  const [frequencies, setFrequencies] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [period, setPeriod] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishingKey, setPublishingKey] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    listClassrooms(year).then((resp) => setClassrooms(resp.data || [])).catch(() => setClassrooms([]));
  }, [year]);

  const classOptions = useMemo(
    () => classrooms.filter((c) => !levelId || c.educationLevel === levelId),
    [classrooms, levelId],
  );

  useEffect(() => {
    setSubjectId("");
    if (!levelId) {
      setSubjects([]);
      return;
    }
    listSubjectsByLevel(levelId).then((resp) => setSubjects(resp.data || [])).catch(() => setSubjects([]));
  }, [levelId]);

  const fetchRows = () => {
    if (!levelId || !classId) {
      setRows([]);
      return;
    }
    setLoading(true);
    // Without a Trimestre, a row's status is the combined status across
    // every trimestre's gradebooks for that subject+turma — filtering by
    // period is done server-side (before the grouping), not client-side,
    // since the grouped response doesn't carry `period` at all.
    groupGradebooks({ levelId, classId, period: period || undefined })
      .then((resp) => setRows(resp.data || []))
      .catch(() => toast.error(t("pauta.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    setExpandedKey(null);
    setDetailsCache({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, classId, period]);

  const filteredRows = useMemo(
    () => rows.filter((r) => !subjectId || r.subjectId === subjectId),
    [rows, subjectId],
  );

  const clearFilters = () => {
    setLevelId("");
    setClassId("");
    setSubjectId("");
    setPeriod("");
  };

  const fetchDetails = async (row) => {
    const key = rowKey(row);
    setDetailsCache((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
    try {
      const [gradebooksResp, evaluationsResp] = await Promise.all([
        listGradebooksBySubject(row.subjectId),
        listEvaluations(row.levelId, row.subjectId),
      ]);
      const gradebooks = (gradebooksResp.data || []).filter((g) => g.classId === row.classId);
      const evaluations = (evaluationsResp.data || []).filter(
        (ev) => !ev.year || String(ev.year) === String(currentYear),
      );
      const evalRows = evaluations.map((ev) => {
        const gb = gradebooks.find((g) => g.evaluation?.id === ev.id || g.evaluationId === ev.id);
        const grades = gb?.grades || [];
        const total = grades.length;
        const graded = grades.filter((g) => isGradeFilled(g.grade)).length;
        return { id: ev.id, evaluationName: ev.evaluationName, graded, missing: total - graded, total, hasGradebook: !!gb };
      });
      const missingEvaluations = evalRows.filter((r) => !r.hasGradebook || r.missing > 0).map((r) => r.evaluationName);
      setDetailsCache((prev) => ({ ...prev, [key]: { rows: evalRows, missingEvaluations, loading: false } }));
    } catch (err) {
      setDetailsCache((prev) => ({ ...prev, [key]: { rows: [], missingEvaluations: [], loading: false, error: true } }));
    }
  };

  const toggleExpand = (row) => {
    const key = rowKey(row);
    setExpandedKey((cur) => (cur === key ? null : key));
    if (!detailsCache[key]) fetchDetails(row);
  };

  const handlePublish = async (row) => {
    if (!(await confirm(t("pauta.confirm.publish"), { variant: "warning" }))) return;
    const key = rowKey(row);
    setPublishingKey(key);
    try {
      await publishGradebooksBySubjectClass(row.subjectId, row.classId);
      toast.success(t("pauta.success.published"));
      fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("pauta.error.publish"));
    } finally {
      setPublishingKey(null);
    }
  };

  const openExcelUrl = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.error(t("pauta.error.export"));
  };

  const handleExportRow = async (row) => {
    const key = rowKey(row);
    setExportingKey(key);
    try {
      const resp = await exportPautaGeralXls({ levelId: row.levelId, classId: row.classId, subjectId: row.subjectId });
      openExcelUrl(resp.data);
    } catch (err) {
      toast.error(t("pauta.error.export"));
    } finally {
      setExportingKey(null);
    }
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const resp = await exportGradebooksXls({ levelId, classId, subjectId: subjectId || undefined });
      openExcelUrl(resp.data);
    } catch (err) {
      toast.error(t("pauta.error.export"));
    } finally {
      setExportingAll(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-16'>
              <div className='d-flex align-items-center gap-2'>
                <span className='w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center text-lg'>
                  <Icon icon='mdi:filter-variant' />
                </span>
                <h6 className='text-md mb-0'>{t("global.filters")}</h6>
              </div>
              <button
                type='button'
                className='btn btn-outline-success-600 btn-sm d-flex align-items-center gap-2'
                disabled={!classId || exportingAll}
                onClick={handleExportAll}
              >
                <Icon icon={exportingAll ? "line-md:loading-twotone-loop" : "mdi:file-excel-outline"} />
                {exportingAll ? t("global.loading") : t("pauta.action.exportAll")}
              </button>
            </div>
            <div className='p-16 radius-8 bg-neutral-50'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                  <select className='form-control form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <select
                    className='form-control form-select'
                    value={levelId}
                    onChange={(e) => {
                      setLevelId(e.target.value);
                      setClassId("");
                    }}
                  >
                    <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("turma.col.className")}</label>
                  <select className='form-control form-select' value={classId} onChange={(e) => setClassId(e.target.value)} disabled={!levelId}>
                    <option value=''>{t("pauta.field.class.placeholder")}</option>
                    {classOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("disciplina.field.subjectName")}</label>
                  <select className='form-control form-select' value={subjectId} onChange={(e) => setSubjectId(e.target.value)} disabled={!levelId}>
                    <option value=''>{t("pauta.filter.allSubjects")}</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.subjectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("nota.filter.period")}</label>
                  <select className='form-control form-select' value={period} onChange={(e) => setPeriod(e.target.value)}>
                    <option value=''>{t("nota.filter.allPeriods")}</option>
                    {PERIOD_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!levelId || !classId ? (
        <div className='col-12'>
          <div className='alert alert-info mb-0'>{t(levelKey("pauta.hint.selectClass"))}</div>
        </div>
      ) : (
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body'>
              {loading ? (
                <div className='text-center py-40'>{t("global.loading")}</div>
              ) : filteredRows.length === 0 ? (
                <div className='alert alert-info mb-0'>{t("pauta.hint.noData")}</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>{t("disciplina.field.subjectName")}</th>
                        <th>{t("turma.col.className")}</th>
                        <th>{t("nota.filter.period")}</th>
                        <th>{t("global.status")}</th>
                        <th>{t("turma.col.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => {
                        const key = rowKey(row);
                        const isExpanded = expandedKey === key;
                        const details = detailsCache[key];
                        // The group's own fields not shown as a column — Nível
                        // is filtered on but never displayed anywhere else.
                        const rowDetails = [{ label: t(levelKey("turma.field.educationLevel")), value: row.level?.description }].filter(
                          (item) => item.value,
                        );
                        return (
                          <React.Fragment key={key}>
                            <tr role='button' onClick={() => toggleExpand(row)} className={isExpanded ? "bg-neutral-50" : ""}>
                              <td>
                                <Icon
                                  icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                                  className='text-secondary-light text-xl'
                                />
                              </td>
                              <td className='fw-semibold text-primary-light'>{row.subject?.name}</td>
                              <td>{row.classroom?.name}</td>
                              <td>{periodLabel(row, t)}</td>
                              <td>
                                <span className={`badge ${GRADEBOOK_STATUS_BADGE[row.status] || "bg-neutral-200 text-neutral-600"}`}>
                                  {t(GRADEBOOK_STATUS_LABEL_KEY[row.status]) || row.status}
                                </span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className='d-flex flex-wrap gap-2'>
                                  {row.status === "FINALIZED" && (
                                    <button
                                      type='button'
                                      className='btn btn-outline-primary-600 btn-sm'
                                      disabled={publishingKey === key}
                                      onClick={() => handlePublish(row)}
                                    >
                                      {t("pauta.action.publish")}
                                    </button>
                                  )}
                                  {(row.status === "DRAFT" ||
                                    row.status === "CHANGE" ||
                                    // Admin, Director Pedagógico e Super Admin podem reabrir a
                                    // edição da pauta seja qual for o estado actual dela.
                                    canEditGradebookAnyStatus()) && (
                                    <button
                                      type='button'
                                      className='btn btn-outline-secondary btn-sm'
                                      onClick={() => navigate("/academico/lista-notas")}
                                    >
                                      {t("pauta.action.goToNotas")}
                                    </button>
                                  )}
                                  <button
                                    type='button'
                                    className='btn btn-outline-success-600 btn-sm d-flex align-items-center gap-2'
                                    disabled={exportingKey === key}
                                    onClick={() => handleExportRow(row)}
                                  >
                                    <Icon icon={exportingKey === key ? "line-md:loading-twotone-loop" : "mdi:file-excel-outline"} />
                                    {exportingKey === key ? t("global.loading") : t("pauta.action.export")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={6} className='p-0'>
                                  <div className='p-24 bg-neutral-25 border-top'>
                                    {rowDetails.length > 0 && (
                                      <div className='table-responsive mb-16'>
                                        <table className='table bordered-table table-sm mb-0 bg-base'>
                                          <thead>
                                            <tr>
                                              {rowDetails.map((item) => (
                                                <th key={item.label}>{item.label}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              {rowDetails.map((item) => (
                                                <td key={item.label}>{item.value}</td>
                                              ))}
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {!details || details.loading ? (
                                      <div className='text-center py-16 text-secondary-light'>{t("global.loading")}</div>
                                    ) : details.rows.length === 0 ? (
                                      <div className='alert alert-info mb-0'>{t("pauta.hint.noEvaluations")}</div>
                                    ) : (
                                      <>
                                        {details.missingEvaluations.length > 0 && (
                                          <div className='alert alert-warning d-flex align-items-start gap-2'>
                                            <Icon icon='mdi:alert-circle-outline' className='text-lg mt-1' />
                                            <span>
                                              {t("pauta.hint.missingEvaluations", {
                                                names: details.missingEvaluations.join(", "),
                                              })}
                                            </span>
                                          </div>
                                        )}
                                        <div className='table-responsive'>
                                          <table className='table bordered-table table-sm mb-0 bg-base'>
                                            <thead>
                                              <tr>
                                                <th>{t("avaliacao.field.name")}</th>
                                                <th>{t("pauta.col.graded")}</th>
                                                <th>{t("pauta.col.missing")}</th>
                                                <th>{t("pauta.col.total")}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {details.rows.map((r) => (
                                                <tr key={r.id}>
                                                  <td>{r.evaluationName}</td>
                                                  <td>{r.graded}</td>
                                                  <td className={!r.hasGradebook || r.missing > 0 ? "text-danger-main fw-semibold" : ""}>
                                                    {r.hasGradebook ? r.missing : t("pauta.hint.noGradebook")}
                                                  </td>
                                                  <td>{r.total}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PautasLayer;
